import React, { useState, useEffect, useContext } from 'react'
import { toast, ToastOptions } from 'react-toastify'
import { Link } from 'react-router-dom'
import {
    Alert,
    Box,
    FormControl,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    Stack,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import { lighten } from '@mui/material/styles'
import SelectList from '@/feature/admin/Templates/select/SelectList'
import QueryBuilderCombinator from './QueryCombinator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog'
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faSave } from '@fortawesome/free-solid-svg-icons/faSave'
import { LoadingDefault } from '@/shared/ui/loading'
import { KeDoContext } from '../../../kedo-context'
import {
    Translation,
    DefDossierItem,
    Combinator,
    Permission,
    QueryObject,
    QueryBuilderEditorProps,
    KeDoContextType,
} from './types'
import AppDialog from '@/shared/ui/app-dialog'
import { CompactButton, StyledButton } from '@/shared/ui/buttons'
import { StyledVerticalTab } from '@/shared/ui/tabs'
import { TabPanel } from '@/shared/ui/tab-panel'
import {
    StyledBreadcrumbs,
    BreadcrumbLink,
    BreadcrumbStatic,
} from '@/shared/ui/breadcrumbs'
import ObjectCreatedInfo from '@/shared/composed-ui/object-created-info/ObjectCreatedInfo'
import { compactSelectSx } from '@/shared/ui/styles'

const QueryBuilderEditor: React.FC<QueryBuilderEditorProps> = (props) => {
    const createEmptyQueryObject = (): QueryObject => ({
        name: '',
        description: '',
        id: 0,
        uuid: '',
        translations: [],
    })
    const toastOptions: ToastOptions = {
        position: 'bottom-center',
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    }
    const { ddId, id, match, history, onSuccess, onClose } = props
    const kedo = useContext<KeDoContextType>(KeDoContext)

    const [errors, setErrors] = useState<Record<string, string | boolean>>({})
    const [editTrans, setEditTrans] = useState(false)
    const [defdossier, setDefDossier] = useState(ddId)
    const [include_archived, setIncludeArchived] = useState('')
    const [ddObject, setDdObject] = useState<{
        id?: number
        name_plural?: string
    }>({})
    const [ddFields, setDdFields] = useState([])
    const [ddLinks, setDdLinks] = useState([])
    const [combinators, setCombinators] = useState<Combinator[]>([])
    const [translations, setTranslations] = useState<Translation[]>([])
    const [loading, setLoading] = useState(false)
    const [permissionUsed, setPermissionUsed] = useState<Permission[]>([])
    const [queryObject, setQueryObject] = useState<QueryObject>(
        createEmptyQueryObject()
    )
    const [showSuccess, setShowSuccess] = useState(false)
    const [saving, setSaving] = useState(false)
    const [tab, setTab] = useState('generic')

    const onSelectDefDossier = (item: DefDossierItem) => {
        setDefDossier(item.id)
        fetchDefDossier(item.id)
    }

    const onDeleteCombinator = (uuid: string) => {
        setCombinators(combinators.filter((item) => item.uuid !== uuid))
    }

    const addQueryCombinator = () => {
        setCombinators([...combinators, { uuid: kedo.utils().uuidv4() }])
    }

    const onChangeCombinator = (index: number, combinator: Combinator) => {
        const newCombinators = [...combinators]
        newCombinators[index] = combinator
        setCombinators(newCombinators)
    }

    const onChangeTranslation = (
        locale: string,
        dashboardName: string | null,
        searchName: string | null
    ) => {
        const transList: Translation[] = [...translations]
        let trans: Translation | undefined = transList.find(
            (item) => item.culture === locale
        )
        const filteredList: Translation[] = transList.filter(
            (item) => item.culture !== locale
        )

        if (!trans) {
            trans = {
                culture: locale,
                dashboard_name: dashboardName,
                search_name: searchName,
            }
        } else if (dashboardName !== null) {
            trans.dashboard_name = dashboardName
        } else if (searchName !== null) {
            trans.search_name = searchName
        }
        setTranslations([...filteredList, trans])
    }

    const applyEmptyTranslationDefaults = () => {
        const trimmedName = queryObject?.name?.trim()
        if (!trimmedName) {
            return
        }

        const mainLocale = kedo.env().getEnvironmentLocales()?.[0]
        if (!mainLocale?.code) {
            return
        }

        const culture = mainLocale.code
        const existing = translations.find((item) => item.culture === culture)
        const updated: Translation = {
            ...existing,
            culture,
            search_name: existing?.search_name?.trim() || trimmedName,
            dashboard_name: existing?.dashboard_name?.trim() || trimmedName,
        }

        setTranslations([
            ...translations.filter((item) => item.culture !== culture),
            updated,
        ])
    }

    const closeSettingsModal = () => {
        applyEmptyTranslationDefaults()
        setEditTrans(false)
    }

    const fetchItem = () => {
        const url = kedo.api().getDossierQueryEndpoint()
        const itemId = match ? match.params.id : id
        kedo.api()
            .get(url + '/' + itemId)
            .then((response) => {
                let combinatorsData = response.data.data.combinators || []
                combinatorsData = combinatorsData.map((item) => {
                    if (!item.uuid) {
                        item.uuid = kedo.utils().uuidv4()
                    }
                    return item
                })

                setDefDossier(response.data.def_dossier.id)
                setDdObject(response.data.def_dossier)
                setIncludeArchived(response.data?.data?.include_archived ?? '')
                setQueryObject(response.data)
                setCombinators(combinatorsData)
                setTranslations(response.data.translations || [])
            })
    }

    const fetchPermissionQuery = async () => {
        try {
            const url = kedo.api().getDefDossierPermissionEndpoint()
            const itemId = match ? match.params.id : id

            const responseRoles = await kedo
                .api()
                .get(kedo.api().getRoleEndpoint())
            const roles = responseRoles.data.results
            const permissionsSet = new Set()
            const uniquePermissions = []

            for (const rol of roles) {
                const { data } = await kedo.api().get(url, {
                    params: { queryId: itemId, roleId: rol.id },
                })

                for (const perm of data.results) {
                    if (!permissionsSet.has(perm.id)) {
                        permissionsSet.add(perm.id)
                        uniquePermissions.push(perm)
                    }
                }
            }
            setPermissionUsed(uniquePermissions)
        } catch (error) {
            console.error(error)
        }
    }

    const fetchDefDossier = (id = defdossier) => {
        const ddUrl = kedo.api().getDefDossierEndpoint()
        kedo.api()
            .getCached(ddUrl + '/' + id)
            .then((response) => {
                setDdObject(response.data)
            })
    }

    const handleShowSuccess = () => {
        setShowSuccess(true)
        setTimeout(() => {
            setShowSuccess(false)
        }, 2000)
    }

    const handleSaveError = (error) => {
        setSaving(false)
        const nameErrors = error?.response?.data?.errors?.name
        if (nameErrors) {
            setErrors((prev) => ({
                ...prev,
                name: Array.isArray(nameErrors) ? nameErrors[0] : nameErrors,
            }))
            setEditTrans(true)
        }

        const message =
            error?.response?.data?.message ?? error?.message ?? kedo.t('Error')
        toast.error(message, toastOptions)
    }

    const save = () => {
        if (!queryObject?.name?.trim()) {
            setErrors((prev) => ({ ...prev, name: true }))
            toast.error(kedo.t('missing_name'), toastOptions)

            return
        }

        setErrors((prev) => ({ ...prev, name: false }))

        const envId = kedo.env().getEnvironment().id
        const url = kedo.api().getDossierQueryEndpoint()
        const queryId = match ? parseInt(match.params.id) : id ?? 0
        const trimmedName = queryObject.name.trim()
        const item = {
            uuid: queryObject?.uuid || kedo.utils().uuidv4(),
            name: trimmedName,
            description: queryObject.description,
            environment: envId,
            defDossier: defdossier,
            data: {
                combinators: combinators,
                include_archived: include_archived,
            },
            translations: translations.map((t) => ({
                ...t,
                search_name: t.search_name?.trim() ?? t.search_name,
                dashboard_name: t.dashboard_name?.trim() ?? t.dashboard_name,
            })),
        }
        setSaving(true)
        if (queryId !== 0) {
            kedo.api()
                .patch(url + '/' + queryId, item)
                .then(() => {
                    kedo.env().reloadEnvironment(envId, kedo.api(), () => {
                        setSaving(false)
                        handleShowSuccess()
                    })
                })
                .catch(handleSaveError)
        } else if (queryId === 0) {
            kedo.api()
                .post(url, item)
                .then((response) => {
                    kedo.env().reloadEnvironment(envId, kedo.api(), () => {
                        setSaving(false)
                        if (onSuccess) {
                            handleShowSuccess()
                            onSuccess(response.data.id)
                        } else {
                            window.location =
                                '/querybuilder/' + response.data.id
                        }
                        if (typeof onClose === 'function') {
                            onClose()
                        }
                    })
                })
                .catch(handleSaveError)
        }
    }

    const renderTranslations = () => {
        const transList = translations || []
        const locales = kedo.env().getEnvironmentLocales()
        if (!locales) {
            return kedo.t('No languages set')
        }

        return locales.map((locale) => {
            let trans: Translation | undefined = transList.find(
                (item) => locale.code === item.culture
            )
            if (!trans) trans = {}

            return (
                <Box key={locale.code} mb={2}>
                    <FormLabel
                        sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 0 }}
                    >
                        {kedo.t('Search name')} ({locale.code})
                    </FormLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={trans?.search_name ?? ''}
                        onChange={(event) =>
                            onChangeTranslation(
                                locale.code,
                                null,
                                event.target.value
                            )
                        }
                        variant="outlined"
                        margin="dense"
                        sx={{ mb: 2 }}
                    />

                    <FormLabel
                        sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 0 }}
                    >
                        {kedo.t('Dashboard name')} ({locale.code})
                    </FormLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={trans?.dashboard_name ?? ''}
                        onChange={(event) =>
                            onChangeTranslation(
                                locale.code,
                                event.target.value,
                                null
                            )
                        }
                        error={!!errors[locale.code]}
                        variant="outlined"
                        margin="dense"
                    />
                </Box>
            )
        })
    }

    const renderCombinators = () => {
        return combinators.map((combinator, comIndex) => (
            <QueryBuilderCombinator
                level={0}
                index={comIndex}
                key={combinator.uuid}
                onDelete={onDeleteCombinator}
                onChange={onChangeCombinator}
                defdossier={defdossier}
                ddObject={ddObject}
                field={combinator}
                kedo={kedo}
            />
        ))
    }

    const renderTranslationsModal = () => {
        return (
            <AppDialog
                open
                maxWidth="lg"
                fullWidth
                title={kedo.t('edit_settings')}
                onClose={closeSettingsModal}
                headerClose={closeSettingsModal}
                actions={
                    <StyledButton
                        onClick={closeSettingsModal}
                        color="secondary"
                    >
                        {kedo.t('Ok')}
                    </StyledButton>
                }
                contentProps={{ dividers: true, dense: true }}
            >
                <Grid
                    container
                    size={{ xs: 12 }}
                    sx={{ p: 0 }}
                    className={'pillsModalBody'}
                >
                    <Grid
                        size={{ xs: 12, sm: 3 }}
                        sx={{ borderRight: 1, borderColor: 'divider' }}
                    >
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            orientation="vertical"
                            sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
                        >
                            <StyledVerticalTab
                                label={kedo.t('Generic')}
                                value="generic"
                            />
                            <StyledVerticalTab
                                label={kedo.t('Info')}
                                value="info"
                            />
                        </Tabs>
                    </Grid>
                    <Grid
                        size={{ xs: 12, sm: 9 }}
                        sx={(theme) => ({ p: 2.5 })}
                        className={'pillsContent'}
                    >
                        <TabPanel
                            value={tab}
                            index="generic"
                            disablePadding={true}
                        >
                            <FormLabel
                                className="required"
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    mb: 0,
                                }}
                            >
                                {kedo.t('Query name')}
                            </FormLabel>
                            <TextField
                                fullWidth
                                size="small"
                                name="name"
                                value={queryObject?.name ?? ''}
                                onChange={(event) => {
                                    setQueryObject((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  name: event.target.value,
                                              }
                                            : prev
                                    )
                                    setErrors((prev) => ({
                                        ...prev,
                                        name: false,
                                    }))
                                }}
                                variant="outlined"
                                margin="dense"
                                sx={{ mb: 2 }}
                                error={!!errors.name}
                                helperText={
                                    typeof errors.name === 'string'
                                        ? errors.name
                                        : kedo.t('query_name_hint')
                                }
                            />
                            <FormLabel
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    mb: 0,
                                }}
                            >
                                {kedo.t('Description')}
                            </FormLabel>
                            <TextField
                                fullWidth
                                size="small"
                                name="description"
                                value={queryObject?.description ?? ''}
                                onChange={(event) =>
                                    setQueryObject((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  description:
                                                      event.target.value,
                                              }
                                            : prev
                                    )
                                }
                                variant="outlined"
                                margin="dense"
                                sx={{ mb: 2 }}
                                error={!!errors.description}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {kedo.t('Translations')}
                            </Typography>
                            <FormControl fullWidth margin="normal">
                                {renderTranslations()}
                            </FormControl>
                        </TabPanel>
                        <TabPanel
                            value={tab}
                            index="info"
                            disablePadding={true}
                        >
                            <ObjectCreatedInfo kedo={kedo} item={queryObject} />
                        </TabPanel>
                    </Grid>
                </Grid>
            </AppDialog>
        )
    }

    const selectDefDossier = () => (
        <Grid size={12}>
            <SelectList
                kedo={kedo}
                title={kedo.t('Select entity')}
                searchName={'search'}
                url={
                    kedo.api().getDefDossierEndpoint() +
                    '?environment=' +
                    kedo.env().getEnvironment().id
                }
                onDisplayName={(item) =>
                    item.id + ' ' + kedo.env().translateItem(item, 'defdossier')
                }
                onSelect={onSelectDefDossier}
            />
        </Grid>
    )

    const renderButtonGroupBar = () => (
        <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
        >
            <Typography
                variant="h5"
                component="label"
                sx={{ minWidth: 80, mr: 1, textAlign: 'right' }}
            >
                Archive
            </Typography>

            <Select
                value={include_archived}
                displayEmpty
                onChange={(e) => setIncludeArchived(e.target.value)}
                size="small"
                sx={{
                    ...compactSelectSx,
                    minWidth: 150,
                    backgroundColor: 'background.paper',
                }}
            >
                <MenuItem value="">{kedo.t('Both')}</MenuItem>
                <MenuItem value="none_archived">
                    {kedo.t('None archived')}
                </MenuItem>
                <MenuItem value="archived">{kedo.t('Archived')}</MenuItem>
            </Select>

            <CompactButton
                dense
                title={kedo.t('Settings')}
                onClick={() => setEditTrans(true)}
            >
                <FontAwesomeIcon icon={faCog} />
            </CompactButton>

            <CompactButton
                dense
                title={'add query combinator'}
                onClick={addQueryCombinator}
            >
                <FontAwesomeIcon icon={faPlus} />
            </CompactButton>
        </Stack>
    )

    useEffect(() => {
        if (
            (match && parseInt(match.params.id) !== 0) ||
            (typeof id === 'number' && id > 0)
        ) {
            fetchItem()
            fetchPermissionQuery()
        } else if (ddId) {
            fetchDefDossier()
        }
    }, [])

    if (loading) {
        return <LoadingDefault />
    }

    return (
        <Box id="kedo-querybuilder" className="mainContainer">
            {editTrans && renderTranslationsModal()}
            <Grid container>
                <Grid size={{ xs: 12 }}>
                    <StyledBreadcrumbs>
                        <BreadcrumbLink component={Link} to="/">
                            {kedo.t('Dashboard')}
                        </BreadcrumbLink>
                        <BreadcrumbLink component={Link} to="/querybuilder">
                            {kedo.t('Queries')}
                        </BreadcrumbLink>
                        <BreadcrumbStatic className="current">
                            {kedo.t('Edit')}
                        </BreadcrumbStatic>
                    </StyledBreadcrumbs>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ mb: 3, mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            {kedo.t('QueryBuilder editor')}
                        </Typography>
                        {defdossier && (
                            <CompactButton
                                onClick={save}
                                startIcon={<FontAwesomeIcon icon={faSave} />}
                            >
                                {saving && (
                                    <LoadingDefault as="span" size="sm" />
                                )}
                            </CompactButton>
                        )}
                    </Stack>
                </Grid>
                {permissionUsed.length > 0 && (
                    <Grid size={12}>
                        <Alert severity="info">
                            {permissionUsed.map((p) => p.role.name).toString()}
                        </Alert>
                    </Grid>
                )}
                {showSuccess && (
                    <Grid size={12}>
                        <Alert
                            className={`k-alert alert alert-success alert-shown`}
                            severity={'success'}
                        >
                            <FontAwesomeIcon icon={faCheck} />{' '}
                            {kedo.t('Item has been saved')}
                        </Alert>
                    </Grid>
                )}
                {!defdossier && selectDefDossier()}
                {defdossier && (
                    <Grid
                        size={12}
                        className="card"
                        sx={{
                            mb: 3,
                            border: 1,
                            borderColor: (theme) =>
                                lighten(theme.palette.primary.light, 0.75),
                            borderStyle: 'solid',
                        }}
                    >
                        <Grid
                            container
                            alignItems={'center'}
                            className="card-header"
                            sx={{
                                py: 1.5,
                                px: 2.5,
                            }}
                        >
                            <Grid>
                                <Typography
                                    variant="h5"
                                    component="h4"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'inherit',
                                    }}
                                >
                                    {ddObject ? ddObject.name_plural : ''}
                                </Typography>
                            </Grid>
                            <Grid size="auto" sx={{ marginLeft: 'auto' }}>
                                {renderButtonGroupBar()}
                            </Grid>
                        </Grid>
                        <Box className="card-body">
                            {defdossier && renderCombinators()}
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

export default QueryBuilderEditor
